import { isAxiosError } from 'axios';
import { type GitlabProvisioningStatusResponse } from 'network/httpClients/GitlabClient';
import { HOSTING_TYPES } from 'network/httpClients/RepositoryClient/api.schema';
import { QUERY_PARAMS } from 'routes/consts';
import { prependHttpsIfNeeded } from '../GithubManifestInstallationDialog/utils';
import {
  CONNECT_ERROR_CODES,
  GENERIC_PROVISIONING_ERROR_KEY,
  GITLAB_ID_ALREADY_CLAIMED_CODE,
  GITLAB_PAT_ERROR_CODES,
  INSTALL_ERROR_CODES,
  MANAGE_RESULT_ERROR_CODES,
  MANAGE_WHOLE_RUN_ERROR_CODES,
  PROVISION_ERROR_CODES,
  SELECT_ERROR_CODES,
  SESSION_EXPIRED_ERROR_KEY,
} from './consts';
import { GITLAB_V2_HOST, type GitlabV2Host } from './types';

const GITLAB_CLOUD_HOST = 'gitlab.com';

/**
 * FE-only host detection: `gitlab.com` (or a subdomain) → cloud, anything else →
 * self-managed. Invalid/empty URLs default to self-managed (the paste-a-token path).
 * The URL is normalized first (a pasted `gitlab.com` with no scheme still parses).
 * @param gitlabUrl - The GitLab URL the user is connecting (may be scheme-less).
 * @returns The detected host bucket.
 */
export const detectHost = (gitlabUrl: string): GitlabV2Host => {
  try {
    const host = new URL(
      prependHttpsIfNeeded(gitlabUrl),
    ).hostname.toLowerCase();
    const isCloud =
      host === GITLAB_CLOUD_HOST || host.endsWith(`.${GITLAB_CLOUD_HOST}`);

    return isCloud ? GITLAB_V2_HOST.CLOUD : GITLAB_V2_HOST.SELF_MANAGED;
  } catch {
    return GITLAB_V2_HOST.SELF_MANAGED;
  }
};

/**
 * Whether the wizard was entered from the SaaS tab (`hostingType=saas`), which
 * means cloud (gitlab.com). Anything else (self-hosted, or no hint) is treated as
 * self-managed. Reads `hostingType` from window.location.search.
 * @returns True when entered from the SaaS tab.
 */
const isSaasEntry = (): boolean =>
  new URLSearchParams(window.location.search).get(QUERY_PARAMS.HOSTING_TYPE) ===
  HOSTING_TYPES.SAAS;

/**
 * The connect-URL placeholder i18n key, chosen by the entry tab: the SaaS tab
 * shows a cloud example, self-hosted (or no hint) a self-managed example.
 * @returns The i18n key suffix under `...steps.connect`.
 */
export const getConnectUrlPlaceholderKey = (): string =>
  isSaasEntry() ? 'gitlab-url.placeholder-cloud' : 'gitlab-url.placeholder';

/**
 * The initial connect-URL value: the SaaS tab prefills a host-only `gitlab.com` (the
 * multi-group discovery flow, editable); self-hosted (or no hint) starts blank so the
 * user pastes their instance root.
 * @returns The prefill URL, or '' when not a SaaS entry.
 */
export const getConnectUrlPrefill = (): string =>
  isSaasEntry() ? 'https://gitlab.com' : '';

/**
 * The connect description i18n key, chosen by the entry tab: the SaaS tab shows
 * the cloud "group Owner" copy, self-hosted (or no hint) the self-managed
 * "instance Admin" copy.
 * @returns The i18n key suffix under `...steps.connect`.
 */
export const getConnectDescriptionKey = (): string =>
  isSaasEntry() ? 'description-cloud' : 'description';

const PAT_QUERY = 'name=Qodo&scopes=api';
/** PAT settings path on GitLab 16.7+ (and gitlab.com). */
const MODERN_PAT_PATH = '/-/user_settings/personal_access_tokens';
/** PAT settings path on GitLab 16.6 and earlier, where the modern path 404s. */
const LEGACY_PAT_PATH = '/-/profile/personal_access_tokens';

/**
 * The instance origin of a (possibly scheme-less) GitLab URL.
 * @param gitlabUrl - The URL the user is connecting (may be scheme-less/empty).
 * @returns The origin, or undefined when the URL isn't parseable yet.
 */
const getGitlabOrigin = (gitlabUrl: string): string | undefined => {
  try {
    return new URL(prependHttpsIfNeeded(gitlabUrl.trim())).origin;
  } catch {
    return undefined;
  }
};

/**
 * The GitLab "create a personal access token" URL for the instance the user is
 * connecting, pre-filled with a Qodo token name + the required `api` scope. Uses
 * the modern path (GitLab 16.7+ / gitlab.com); falls back to gitlab.com when the
 * URL isn't parseable yet.
 * @param gitlabUrl - The URL the user is connecting (may be scheme-less/empty).
 * @returns An absolute URL to that instance's PAT settings page.
 */
export const getPatCreationUrl = (gitlabUrl: string): string => {
  const origin = getGitlabOrigin(gitlabUrl) ?? 'https://gitlab.com';

  return `${origin}${MODERN_PAT_PATH}?${PAT_QUERY}`;
};

/**
 * The legacy PAT settings URL (`/-/profile/...`) for self-managed GitLab 16.6 and
 * earlier, where {@link getPatCreationUrl}'s modern path 404s. Shown only as a
 * fallback beneath the primary link: undefined for cloud (always modern) and for
 * URLs without a parseable origin, so callers render nothing in those cases.
 * @param gitlabUrl - The URL the user is connecting (may be scheme-less/empty).
 * @returns An absolute legacy PAT URL, or undefined when no fallback applies.
 */
export const getLegacyPatCreationUrl = (
  gitlabUrl: string,
): string | undefined => {
  const trimmed = gitlabUrl.trim();

  if (detectHost(trimmed) !== GITLAB_V2_HOST.SELF_MANAGED) return undefined;

  const origin = getGitlabOrigin(trimmed);

  return origin ? `${origin}${LEGACY_PAT_PATH}?${PAT_QUERY}` : undefined;
};

/** Reasons the connect URL fails FE structural validation; each maps to i18n copy. */
export const CONNECT_URL_ERROR = {
  EMPTY: 'empty',
  MALFORMED: 'malformed',
  CLOUD_PATH_TOO_DEEP: 'cloud-path-too-deep',
  SELF_MANAGED_HAS_PATH: 'self-managed-has-path',
} as const;

export type ConnectUrlError =
  (typeof CONNECT_URL_ERROR)[keyof typeof CONNECT_URL_ERROR];

export type ConnectUrlValidation =
  | { isValid: true; host: GitlabV2Host }
  | { isValid: false; error: ConnectUrlError };

/**
 * FE structural validation of the connect URL (UX-only; the BE is authoritative).
 * Cloud accepts a bare host (`https://gitlab.com`, the multi-group discovery flow) or a
 * single top-level group (legacy full URL); a deeper path (subgroup/repo) is rejected.
 * Self-managed requires a bare root host with no path. Scheme is optional (normalized first).
 * @param gitlabUrl - The URL the user pasted (may be scheme-less).
 * @returns A discriminated result: valid with the detected host, or the failure reason.
 */
export const validateConnectUrl = (gitlabUrl: string): ConnectUrlValidation => {
  const trimmed = gitlabUrl.trim();

  if (!trimmed) {
    return { isValid: false, error: CONNECT_URL_ERROR.EMPTY };
  }

  let parsed: URL;

  try {
    parsed = new URL(prependHttpsIfNeeded(trimmed));
  } catch {
    return { isValid: false, error: CONNECT_URL_ERROR.MALFORMED };
  }

  const host = detectHost(trimmed);
  const segments = parsed.pathname.split('/').filter(Boolean);

  if (host === GITLAB_V2_HOST.CLOUD) {
    // Host-only and a single top-level group are both valid connect hosts; anything
    // deeper (a subgroup or project path) is not.
    if (segments.length > 1) {
      return { isValid: false, error: CONNECT_URL_ERROR.CLOUD_PATH_TOO_DEEP };
    }

    return { isValid: true, host };
  }

  if (segments.length > 0) {
    return { isValid: false, error: CONNECT_URL_ERROR.SELF_MANAGED_HAS_PATH };
  }

  return { isValid: true, host };
};

/**
 * Reads the typed provisioning error `code` from a failed request. Exception-path
 * failures (connect / provision-start / listing) carry the code on the standard
 * envelope field `error.internal_status_code` — the global handler flattens the
 * human text into `error.message` (a `"422: {…}"` string) and does not expose a
 * structured `error.code`, so read `internal_status_code`. The `error.code` /
 * `detail.code` / flat `code` fallbacks cover status-poll bodies and any
 * pre-envelope shape. Consumers map the code to reason-specific copy.
 * @param err - The error thrown by the connect/provision mutation.
 * @returns The error code string, or undefined if not an axios error with a code.
 */
export const getProvisioningErrorCode = (err: unknown): string | undefined => {
  if (!isAxiosError(err)) return undefined;

  const data = err.response?.data;
  const code =
    data?.error?.internal_status_code ??
    data?.error?.code ??
    data?.detail?.code ??
    data?.code;

  return typeof code === 'string' ? code : undefined;
};

/**
 * The claim ("already connected elsewhere") copy is host-aware: cloud claims a
 * top-level group, self-hosted the whole instance. Appends the self-managed
 * variant suffix for a self-managed host; every other key is unchanged. The host
 * is the one detected from the connected URL (or BE-reported), not the entry tab.
 * @param key - The resolved error-key suffix.
 * @param host - The connected GitLab host (cloud vs self-managed).
 * @returns The key, host-variant-adjusted for the claim conflict.
 */
const withClaimHostVariant = (key: string, host: GitlabV2Host): string =>
  key === GITLAB_ID_ALREADY_CLAIMED_CODE && host === GITLAB_V2_HOST.SELF_MANAGED
    ? `${key}-self-managed`
    : key;

/**
 * Maps a failed connect request to an i18n error-key suffix: the typed code when
 * it's one we render specific copy for, else the generic fallback.
 * @param err - The error thrown by the connect mutation.
 * @param host - The connected host, used to pick the claim-conflict copy variant.
 * @returns A known connect error code, or the generic key.
 */
export const getConnectErrorKey = (
  err: unknown,
  host: GitlabV2Host,
): string => {
  const code = getProvisioningErrorCode(err);
  const key =
    code && (CONNECT_ERROR_CODES as readonly string[]).includes(code)
      ? code
      : GENERIC_PROVISIONING_ERROR_KEY;

  return withClaimHostVariant(key, host);
};

/**
 * The typed Select-listing error code (from the 422 envelope) when it's one the
 * Select step renders reason-specific copy for, else undefined. Undefined means an
 * untyped / transient failure, which the caller renders as the generic message
 * with Retry; a typed code renders reason-specific copy without Retry (retry can't
 * fix a dead token or a gone scope). The code doubles as its i18n key suffix under
 * `steps.select.errors`.
 * @param err - The error thrown by the Select listing query.
 * @returns A known select error code, or undefined for untyped/transient failures.
 */
export const getSelectErrorCode = (err: unknown): string | undefined => {
  const code = getProvisioningErrorCode(err);

  return code && (SELECT_ERROR_CODES as readonly string[]).includes(code)
    ? code
    : undefined;
};

/**
 * Maps a provision-status failure code to an i18n error-key suffix: the code when
 * it's one we render specific copy for, else the generic fallback.
 * @param code - The `error.code` from a failed provision status (may be undefined).
 * @param host - The connected host, used to pick the claim-conflict copy variant.
 * @returns A known provision error code, or the generic key.
 */
export const getProvisionErrorKey = (
  code: string | undefined,
  host: GitlabV2Host,
): string => {
  const key =
    code && (PROVISION_ERROR_CODES as readonly string[]).includes(code)
      ? code
      : GENERIC_PROVISIONING_ERROR_KEY;

  return withClaimHostVariant(key, host);
};

/**
 * Maps a per-item install-failure code to an i18n error-key suffix: the code
 * when known, else the generic fallback.
 * @param code - The per-item `error` code from an install result; a missing or
 *   unknown code (unexpected failure) maps to the generic key.
 * @returns A known install error code, or the generic key.
 */
export const getInstallErrorKey = (code: string | null | undefined): string =>
  code && (INSTALL_ERROR_CODES as readonly string[]).includes(code)
    ? code
    : GENERIC_PROVISIONING_ERROR_KEY;

/**
 * Maps a per-scope manage-result failure code (add or remove branch) to an i18n
 * error-key suffix: the code when known, else the generic fallback.
 * @param code - The per-scope `error` code from a manage result; a missing or
 *   unknown code maps to the generic key.
 * @returns A known manage error code, or the generic key.
 */
export const getManageResultErrorKey = (
  code: string | null | undefined,
): string =>
  code && (MANAGE_RESULT_ERROR_CODES as readonly string[]).includes(code)
    ? code
    : GENERIC_PROVISIONING_ERROR_KEY;

/**
 * Whether a failed list/manage error code is a PAT-token error (bad or
 * under-scoped token) — Manage surfaces these at the PAT field rather than as a
 * generic on-screen error.
 * @param code - The typed error code (may be undefined).
 * @returns True for `invalid_token` / `insufficient_scope`.
 */
export const isPatErrorCode = (code: string | undefined): boolean =>
  code !== undefined &&
  (GITLAB_PAT_ERROR_CODES as readonly string[]).includes(code);

/**
 * Maps a whole-run manage-status failure code (surfaced on a `failed` status,
 * not per-scope) to an i18n error-key suffix: the code when known
 * (`invalid_token` / `insufficient_scope` / `upstream_error`), else the generic
 * fallback.
 * @param code - The whole-run `error.code` from a failed manage status.
 * @returns A known whole-run error code, or the generic key.
 */
export const getManageWholeRunErrorKey = (
  code: string | null | undefined,
): string =>
  code && (MANAGE_WHOLE_RUN_ERROR_CODES as readonly string[]).includes(code)
    ? code
    : GENERIC_PROVISIONING_ERROR_KEY;

/**
 * Maps a transport failure to an i18n error-key suffix. A `sessionLostStatus`
 * response (provision 409 / status 404 = the transient Connect session expired)
 * maps to the session-expired key; anything else to the generic key.
 * @param err - The error thrown by the provision mutation or status query.
 * @param sessionLostStatus - The HTTP status that means the session was lost (409 or 404).
 * @returns The session-expired key, or the generic key.
 */
export const getTransportErrorKey = (
  err: unknown,
  sessionLostStatus: number,
): string =>
  isAxiosError(err) && err.response?.status === sessionLostStatus
    ? SESSION_EXPIRED_ERROR_KEY
    : GENERIC_PROVISIONING_ERROR_KEY;

/**
 * The single-install path (self-managed / legacy single-group cloud) provisions one
 * top-level group, so its outcome is the sole entry of the batch `results` list.
 * @param status - The provision-status response.
 * @returns The one provision result, or undefined if the list is empty.
 */
export const getSingleProvisionResult = (
  status: GitlabProvisioningStatusResponse,
) => status.results[0];
